import {NavLink, useHistory} from "react-router-dom";
import styles from "./mobileSideBar.module.scss";
import iconResidents from "@source/assets/icons/iconResidents.svg";
import iconHistory from "@source/assets/icons/iconHistory.svg";
import iconAnnouncements from "@source/assets/icons/iconAnnouncements.svg";
import iconEditResident from "@source/assets/icons/iconEditResident.svg";
import iconArchive from "@source/assets/icons/iconArchive.svg";
import iconXslxFile from "@source/assets/icons/iconXlsxFile.svg";
import iconHelp from "@source/assets/icons/iconHelp.svg";
import iconHouseFilled from "@source/assets/icons/iconHouseFilled.svg";
import iconClose from '@source/assets/icons/iconClose.svg';
import {HOME_ROUTES} from "@source/utils/utils";
import Svg from "@source/components/UI/svg/svg";
import userStore from "@source/store/userStore";
import nursingHomeStore from "@source/store/nursingHomeStore";
import {observer} from "mobx-react-lite";
import Loader from "@source/components/UI/loader/loader";
import {HomePermissionsEnum, OrganizationPermissionsEnum} from "@source/api/models/permissionsModels";
import CacheImg from "@components/UI/cacheImg/cacheImg";
import Dropdown, {IDropdownItem} from "@components/UI/dropdown/dropdown";
import {INursingHome} from "@source/api/models/nursingHomeModels";
import {useEffect, useState} from "react";
import {useFirebaseLogEvent} from "@source/utils/hooks/useFirebaseLogEvent";
import openMenuBack from "@assets/icons/openMenuBack.svg";
import openMenuForward from "@assets/icons/openMenuForward.svg";
import iconLightbulb from "@assets/icons/iconLightbulb.svg";
import {canShowConsentNavigation} from "@source/utils/consentAccess";

const MobileSideBar = observer((props: MobileSideBarProps) => {
  const {isOpen, onClose} = props;
  const router = useHistory();

  const [dropdownItems, setDropdownItems] = useState<IDropdownItem[]>([]);
  const [animationState, setAnimationState] = useState<'entering' | 'entered' | 'exiting'>('entering');
  const [logEvent] = useFirebaseLogEvent();

  useEffect(() => {
    const newDropdownItems = nursingHomeStore.nursingHomes.map((nursingHome) => {
      return {
        onClick: () => onNursingHomeClick(nursingHome),
        text: nursingHome.name,
      }
    })

    setDropdownItems(newDropdownItems);
  }, [nursingHomeStore.nursingHomes])

  // Animation effect - trigger entering animation on mount
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setAnimationState('entered');
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimationState('exiting');
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 150); // Match the CSS transition duration
  };

  const navigateToSettings = () => {
    router.push(HOME_ROUTES.Settings);
    handleClose();
  }

  const onNursingHomeClick = (nursingHome: INursingHome) => {
    logEvent('change_home', {
      home: nursingHome.name,
      organization: nursingHome.organization.name,
    })

    nursingHomeStore.setActiveNursingHome(nursingHome);
    nursingHomeStore.setLocalStorageActiveNursingHome(nursingHome._id);
  }

  const isShowAdminPanelLink = () => {
    return userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PASSWORD) ||
    userStore.isPermission(OrganizationPermissionsEnum.ENABLE_EMPLOYEE) ||
    userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_EMPLOYEE) ||
    userStore.isPermission(OrganizationPermissionsEnum.DISABLE_EMPLOYEE) ||
    userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PHOTO) ||
    userStore.isPermission(OrganizationPermissionsEnum.RESEND_FAMILY_INVITATION) ||
    userStore.isPermission(OrganizationPermissionsEnum.MARK_RESIDENTS_AS_NON_PARTICIPANT) ||
    userStore.isPermission(OrganizationPermissionsEnum.RESEND_EMPLOYEE_INVITATION) ||
    userStore.isPermission(OrganizationPermissionsEnum.DISABLE_FAMILY_MEMBER) ||
    userStore.isPermission(OrganizationPermissionsEnum.ENABLE_FAMILY_MEMBER) ||
    userStore.isPermission(OrganizationPermissionsEnum.CREATE_ACCESS_KEYS) ||
    userStore.isPermission(OrganizationPermissionsEnum.UPDATE_ACCESS_KEYS) ||
    userStore.isPermission(OrganizationPermissionsEnum.VIEW_ACCESS_KEYS) ||
    userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_HOUSE)
  }

  const getAdminLinkRoute = () => {
    if (
      userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PASSWORD) ||
      userStore.isPermission(OrganizationPermissionsEnum.ENABLE_EMPLOYEE) ||
      userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_EMPLOYEE) ||
      userStore.isPermission(OrganizationPermissionsEnum.DISABLE_EMPLOYEE) ||
      userStore.isPermission(OrganizationPermissionsEnum.EDIT_EMPLOYEE_PHOTO) ||
      userStore.isPermission(OrganizationPermissionsEnum.RESEND_EMPLOYEE_INVITATION)
    ) {
      return HOME_ROUTES.AdminPanelStaff;
    }

    if (
      userStore.isPermission(OrganizationPermissionsEnum.DISABLE_FAMILY_MEMBER) ||
      userStore.isPermission(OrganizationPermissionsEnum.ENABLE_FAMILY_MEMBER) ||
      userStore.isPermission(OrganizationPermissionsEnum.MARK_RESIDENTS_AS_NON_PARTICIPANT) ||
      userStore.isPermission(OrganizationPermissionsEnum.RESEND_FAMILY_INVITATION)
    ) {
      return HOME_ROUTES.AdminPanelFamilyMembers;
    }

    if (
      userStore.isPermission(OrganizationPermissionsEnum.CREATE_ACCESS_KEYS) ||
      userStore.isPermission(OrganizationPermissionsEnum.UPDATE_ACCESS_KEYS) ||
      userStore.isPermission(OrganizationPermissionsEnum.VIEW_ACCESS_KEYS) ||
      userStore.isPermission(OrganizationPermissionsEnum.ADD_NEW_HOUSE)
    ) {
      return HOME_ROUTES.AdminPanelHouses;
    }

    return "";
  }

  const getInformationLinkRoute = () => {
    if(
        userStore.isPermission(HomePermissionsEnum.DOWNLOAD_UPDATE_HISTORY) ||
        userStore.isPermission(HomePermissionsEnum.DOWNLOAD_UNREAD_UPDATES) ||
        userStore.isPermission(HomePermissionsEnum.VIEW_HOME_STATISTIC)
    ) {
      return HOME_ROUTES.InformationExports;
    }
    return "";
  }

  const handleNavClick = () => {
    handleClose();
  }

  if (!isOpen) return null;

  return(
    <div className={styles.overlay}>
      <div className={styles.mobileSideBar}>
        {/* Header with Hide menu and Home dropdown */}
        <div className={styles.header}>
          <div className={styles.hideMenu} onClick={handleClose}>
            <img className={styles.toggle__btn} src={openMenuBack}/>
            <span className={styles.hideMenuText}>Hide menu</span>
          </div>

          <div className={styles.homeDropdown}>
            <img className={styles.icon} src={iconHouseFilled} alt="home"/>
            <Dropdown className={styles.dropdown} items={dropdownItems} arrow>
              <span className={styles.dropdownText}>{nursingHomeStore.activeNursingHome?.name}</span>
            </Dropdown>
          </div>
        </div>

        {/* Navigation */}
        <div className={`${styles.navAndFooter} ${animationState === 'entered' ? styles.entering : animationState === 'exiting' ? styles.exiting : ''}`}>
          <div className={styles.nav}>
            {
              userStore.isPermission(HomePermissionsEnum.VIEW_RESIDENTS) ?
                  <NavLink to={HOME_ROUTES.Residents} className={styles.navItem}
                           activeClassName={styles.navItemActive} onClick={handleNavClick}>
                    <div className={styles.iconWrapper}>
                      <Svg className={styles.icon} src={iconResidents}/>
                    </div>
                    <span className={styles.text}>Residents</span>
                  </NavLink>
                  :
                  <></>
            }

            {
              userStore.isPermission(HomePermissionsEnum.VIEW_UPDATES) ?
                  <NavLink to={HOME_ROUTES.History} className={styles.navItem}
                           activeClassName={styles.navItemActive} onClick={handleNavClick}>
                    <div className={styles.iconWrapper}>
                      <Svg className={styles.icon} src={iconHistory}/>
                    </div>
                    <span className={styles.text}>Updates history</span>
                  </NavLink>
                  :
                  <></>
            }

            {canShowConsentNavigation() &&
              <NavLink to={HOME_ROUTES.ConsentDocuments} className={styles.navItem}
                       activeClassName={styles.navItemActive} onClick={handleNavClick}>
                <div className={styles.iconWrapper}>
                  <Svg className={styles.icon} src={iconHistory}/>
                </div>
                <span className={styles.text}>Consent documents</span>
              </NavLink>
            }

            {
              userStore.isPermission(HomePermissionsEnum.VIEW_ANNOUNCEMENT_EVENT) ?
                  <NavLink to={HOME_ROUTES.Announcements} className={styles.navItem}
                           activeClassName={styles.navItemActive} onClick={handleNavClick}>
                    <div className={styles.iconWrapper}>
                      <Svg className={styles.icon} src={iconAnnouncements}/>
                    </div>
                    <span className={styles.text}>Announcements</span>
                  </NavLink>
                  :
                  <></>
            }

            {
              isShowAdminPanelLink() ?
                  <NavLink to={getAdminLinkRoute()} exact={false} className={styles.navItem}
                           activeClassName={styles.navItemActive} onClick={handleNavClick}>
                    <div className={styles.iconWrapper}>
                      <Svg className={styles.icon} src={iconEditResident}/>
                    </div>
                    <span className={styles.text}>Admin panel</span>
                  </NavLink>
                  :
                  <></>
            }

            {
              userStore.isPermission(HomePermissionsEnum.VIEW_DISABLED_RESIDENT) ?
                  <NavLink to={HOME_ROUTES.ResidentsArchive} exact={false} className={styles.navItem}
                           activeClassName={styles.navItemActive} onClick={handleNavClick}>
                    <div className={styles.iconWrapper}>
                      <Svg className={styles.icon} src={iconArchive}/>
                    </div>
                    <span className={styles.text}>Archive</span>
                  </NavLink>
                  :
                  <></>
            }

            {
                userStore.isPermission(HomePermissionsEnum.VIEW_HOME_STATISTIC) &&
                <NavLink to={getInformationLinkRoute()} exact={false} className={styles.navItem}
                         activeClassName={styles.navItemActive} onClick={handleNavClick}>
                  <div className={styles.iconWrapper}>
                    <Svg className={styles.icon} src={iconXslxFile}/>
                  </div>
                  <span className={styles.text}>Information</span>
                </NavLink>
            }

            <NavLink to={HOME_ROUTES.Help} exact={false} className={styles.navItem}
                     activeClassName={styles.navItemActive} onClick={handleNavClick}>
              <div className={styles.iconWrapper}>
                <Svg className={styles.icon} src={iconLightbulb}/>
              </div>
              <span className={styles.text}>Knowledge Base</span>
            </NavLink>
          </div>

          {/* Footer with user info */}
          <div className={styles.footer}>
            {
              userStore.getId() ?
                  <div className={styles.user} onClick={navigateToSettings}>
                    <CacheImg
                        url={userStore.getUserInfo()?.photo}
                        alt="userPhoto"
                        className={styles.userPhoto}
                        imgClassName={styles.userPhoto}
                    >
                      <div className={styles.userAvatar}>
                        {userStore.getUserInfo()?.firstName[0] || "" + userStore.getUserInfo()?.lastName[0] || ""}
                      </div>
                    </CacheImg>

                    <div className={styles.userInfo}>
                    <span className={styles.name}>
                      <span>{userStore.getUserInfo()?.firstName} {userStore.getUserInfo()?.lastName}</span>
                    </span>
                      <span className={styles.role}>{userStore.getUserInfo()?.account?.position || '-'}</span>
                    </div>
                  </div>
                  :
                  <Loader className={styles.loader} height={25} width={25} borderWidth={3}/>
            }
          </div>
        </div>
      </div>
    </div>
  )
})

export default MobileSideBar;

interface MobileSideBarProps {
  isOpen: boolean;
  onClose: () => void;
}